/**
 * BaseTableTabs – pill-style status/category filters for table views.
 * Renders .tw-tabs wrapper with .nv-tab.nav_status divs (data-tabs-status, fa-check icon).
 * Uses rebrand/themes/purple/_tabs.scss; matches legacy markup for change_status integration.
 */

import React, { useState, useEffect } from 'react'

export interface BaseTableFilterOption {
    value: string
    label: string
}

export interface BaseTableTabsProps {
    /** Filter options (value + label; label shown in tab) */
    options: BaseTableFilterOption[]
    /** Currently selected value */
    value: string
    /** Called when user selects a filter (use from React) */
    onChange?: (value: string) => void
    /** Global function name for PHP/mount (e.g. 'page_table_load'); called as fn('', '', value) */
    onChangeCallback?: string
    /** DOM input id to update before calling onChangeCallback (e.g. 'table_tab_status') */
    filterInputId?: string
    /** Optional class name for the wrapper */
    className?: string
    /** Optional id for the wrapper (e.g. for aria-describedby) */
    id?: string

    orderAlphabetical?: boolean
}

// Alias so index.tsx can re-export as BaseTableFilters
export type BaseTableFiltersProps = BaseTableTabsProps

export function BaseTableTabs({
    options,
    value,
    onChange,
    onChangeCallback,
    filterInputId,
    className,
    orderAlphabetical = true,
    id,
}: BaseTableTabsProps) {
    const [selected, setSelected] = useState(value)
    const displayOptions = orderAlphabetical
        ? [...options].sort((a, b) => a.label.localeCompare(b.label))
        : options

    useEffect(() => { setSelected(value) }, [value])

    const handleChange = (newValue: string) => {
        setSelected(newValue)
        // Sync the hidden DOM input so update_address_bar()/query_string() picks up the new value
        if (filterInputId) {
            const el = document.getElementById(filterInputId) as HTMLInputElement | null
            if (el) el.value = newValue
        }
        if (onChangeCallback && typeof (window as unknown as Record<string, (a: string, b: string, c: string) => void>)[onChangeCallback] === 'function') {
            (window as unknown as Record<string, (a: string, b: string, c: string) => void>)[onChangeCallback]('', '', newValue)
        } else if (onChange) {
            onChange(newValue)
        }
    }
    const wrapperClass = ['tw-tabs', className].filter(Boolean).join(' ')
    return (
        <div
            id={id}
            className={wrapperClass}
            role="tablist"
            aria-label="Filter by status"
        >
            {displayOptions.map((opt) => {
                const isActive = selected === opt.value
                const tabClass = ['nv-tab', 'nav_status', 'tab_status', isActive ? 'active' : ''].filter(Boolean).join(' ')
                return (
                    <div
                        key={opt.value}
                        id={`tab_status_${opt.value}`}
                        className={tabClass}
                        data-tabs-status={opt.value}
                        role="tab"
                        aria-selected={isActive}
                        aria-label={opt.label}
                        onClick={() => handleChange(opt.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                handleChange(opt.value)
                            }
                        }}
                        tabIndex={0}
                    >
                        <i className="fa fa-check tab-check" aria-hidden />
                        {opt.label}
                    </div>
                )
            })}
        </div>
    )
}

// Re-export under the name used by index.tsx and PHP callers
export { BaseTableTabs as BaseTableFilters }
